import React from "react";

import { useStrings } from "../../../../localization/localizationHelpers";
import { CrmGameConceptStatus } from "../../../../models/crmStatus";
import { GameConceptPageData } from "../../../../redux/actions/gameConceptPageActions";
import * as styles from "../../GameConceptPage.module.css";

type GameConceptHeaderProps = Readonly<{
  conceptState: GameConceptPageData["state"];
  isDraft: boolean;
  allSectionsValid: boolean;
  isReadOnly: boolean;
  isBusy: boolean;
  isSaveInProgress: boolean;
  isSubmitInProgress: boolean;
  isSubmitSavePending: boolean;
  isOnboardingApproved: boolean;
  isSellerEligible: boolean;
  canSubmit: boolean;
  onCancel: () => void;
  onSave: () => void;
  onSubmit: () => void;
}>;

export const GameConceptHeader = ({
  conceptState,
  isDraft,
  allSectionsValid,
  isReadOnly,
  isBusy,
  isSaveInProgress,
  isSubmitInProgress,
  isSubmitSavePending,
  isOnboardingApproved,
  isSellerEligible,
  canSubmit,
  onCancel,
  onSave,
  onSubmit,
}: GameConceptHeaderProps) => {
  const [
    underReviewLabel,
    onHoldLabel,
    needsAttentionLabel,
    contractsSentLabel,
    approvedLabel,
    rejectedLabel,
    readyToSubmitBadge,
    draftBadge,
    cancelButton,
    saveAndCloseButton,
    savingButton,
    submittingButton,
    submitButton,
  ] = useStrings(
    "underReview",
    "onHold",
    "needsAttentionLabel",
    "contractsSentLabel",
    "approved",
    "rejected",
    "gameConceptReadyToSubmitBadge",
    "draft",
    "cancelButton",
    "saveAndCloseButton",
    "savingButton",
    "submittingButton",
    "xboxOnboardSubmitButton"
  );

  const statusDisplayNames: Record<string, string> = {
    [CrmGameConceptStatus.UnderReview]: underReviewLabel,
    [CrmGameConceptStatus.OnHold]: onHoldLabel,
    [CrmGameConceptStatus.MoreInfoNeeded]: needsAttentionLabel,
    [CrmGameConceptStatus.AdditionalContractsSent]: contractsSentLabel,
    [CrmGameConceptStatus.Accepted]: approvedLabel,
    [CrmGameConceptStatus.Rejected]: rejectedLabel,
  };

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        {conceptState && !isDraft && conceptState.status !== CrmGameConceptStatus.Unset && (
          <span
            className={`${styles.statusBadge} ${
              conceptState.status === CrmGameConceptStatus.Rejected ? styles.rejectedBadge : ""
            }`}
          >
            {statusDisplayNames[conceptState.status] ?? conceptState.status}
          </span>
        )}
        {(isDraft || !conceptState || conceptState.status === CrmGameConceptStatus.Unset) &&
          (allSectionsValid && isSellerEligible ? (
            <span className={styles.readyBadge}>{readyToSubmitBadge}</span>
          ) : (
            <span className={styles.draftBadge}>{draftBadge}</span>
          ))}
      </div>
      <div className={styles.headerRight}>
        {!isReadOnly && (
          <>
            <button className={styles.cancelButton} onClick={onCancel} disabled={isBusy}>
              {cancelButton}
            </button>
            <button className={styles.saveButton} onClick={onSave} disabled={isBusy}>
              {isSaveInProgress ? savingButton : saveAndCloseButton}
            </button>
            {isOnboardingApproved && (
              <button
                className={styles.submitButton}
                onClick={onSubmit}
                disabled={!canSubmit || isBusy || !allSectionsValid || !isSellerEligible}
              >
                {isSubmitSavePending
                  ? savingButton
                  : isSubmitInProgress
                  ? submittingButton
                  : submitButton}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
